import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Link, useParams } from 'react-router-dom';
import { BookOpen, ArrowLeft, Loader, AlertCircle, Calendar, User } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Course Section Create Page
 * Allows admins and moderators to create a new course section
 */
const CourseSectionCreatePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get courseId from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const { courseId } = useParams();
  
  // Component state
  const [course, setCourse] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [sectionForm, setSectionForm] = useState({
    courseId: courseId || '',
    professorId: '',
    semester: 'Fall',
    year: new Date().getFullYear()
  });
  
  // Check if user is admin or moderator
  const isAuthorized = user && (user.role === 'admin' || user.role === 'moderator');
  
  useEffect(() => {
    if (!isAuthorized) {
      toast.error('You do not have permission to create course sections');
      navigate('/school');
      return;
    }
    
    if (!courseId) {
      setError('No course ID provided');
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch course details
        const courseRes = await fetch(`${process.env.REACT_APP_API_URL}/api/courses/${courseId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!courseRes.ok) {
          throw new Error('Failed to fetch course details');
        }
        
        const courseData = await courseRes.json();
        setCourse(courseData);
        
        // Fetch professors for the school
        const professorsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/schools/${courseData.schoolId}/professors`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!professorsRes.ok) {
          throw new Error('Failed to fetch professors');
        }
        
        const professorsData = await professorsRes.json();
        setProfessors(professorsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'An error occurred while loading data');
        toast.error('Failed to load required data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [courseId, isAuthorized, navigate]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSectionForm(prev => ({
      ...prev,
      [name]: name === 'year' ? parseInt(value, 10) : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!sectionForm.professorId || !sectionForm.semester || !sectionForm.year) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Submit form to API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/course-sections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sectionForm)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create course section');
      }
      
      const newSection = await response.json();
      
      toast.success('Course section created successfully');
      navigate(`/courses/${courseId}`);
    } catch (error) {
      console.error('Error creating course section:', error);
      toast.error(error.message || 'Failed to create course section');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen p-4">
        <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Error
          </h2>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  if (!course) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Course Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The course you're trying to add a section to could not be found.
          </p>
          <Link
            to="/courses"
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            View All Courses
          </Link>
        </div>
      </div>
    );
  }
  
  // Generate year options (current year and next 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + i);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          <span>Back to Course</span>
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-start">
          <BookOpen className="h-8 w-8 text-red-500 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Add Section for {course.code}: {course.name}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {course.departmentName} ({course.departmentCode})
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Section Details
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="professorId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Professor <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="professorId"
                  name="professorId"
                  value={sectionForm.professorId}
                  onChange={handleChange}
                  className="pl-10 block w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">Select a professor</option>
                  {professors.map(professor => (
                    <option key={professor.id} value={professor.id}>
                      {professor.name}
                    </option>
                  ))}
                </select>
              </div>
              {professors.length === 0 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  No professors available. Please add professors first.
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="semester" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Semester <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="semester"
                  name="semester"
                  value={sectionForm.semester}
                  onChange={handleChange}
                  className="pl-10 block w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="Fall">Fall</option>
                  <option value="Spring">Spring</option>
                  <option value="Summer">Summer</option>
                  <option value="Winter">Winter</option>
                </select>
              </div>
            </div>
            
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Year <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="year"
                  name="year"
                  value={sectionForm.year}
                  onChange={handleChange}
                  className="pl-10 block w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/courses/${courseId}`)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || professors.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader className="inline h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                'Create Section'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseSectionCreatePage;